import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import api from '@/services/api'
import type { BgImageResponse, BgImageCanvasParams } from '@/types/bgImage'

// 背景画像メタデータを取得
// robotId を渡すと同一サイト内でその機体が publish した画像に限定する
function useBgImageData(
  siteId: string | undefined,
  timestamp?: string,
  robotId?: string
) {
  return useQuery({
    queryKey: ['bgImage', siteId, robotId, timestamp],
    queryFn: async () => {
      const params: Record<string, string> = { site_id: siteId! }
      if (timestamp) params.timestamp = timestamp
      if (robotId) params.robot_id = robotId
      const { data } = await api.get<BgImageResponse>('/bg-image', { params })
      return data
    },
    enabled: !!siteId,
    staleTime: 1000 * 60 * 5, // 背景画像は頻繁に変わらないため5分
    gcTime: 1000 * 60 * 30,
    // timestamp が変わるたびにキーが変わるため、フェッチ中も前の画像を
    // 表示し続ける（これがないと再生中に毎分背景が消えて点滅する）
    placeholderData: keepPreviousData,
  })
}

export interface UseBgImageResult {
  bgImage: HTMLImageElement | null
  canvasParams: BgImageCanvasParams | null
  isLoading: boolean
}

// 背景画像を取得し、HTMLImageElementとして読み込む
export function useBgImage(
  siteId: string | undefined,
  timestamp?: string,
  robotId?: string
): UseBgImageResult {
  const { data, isLoading: isMetaLoading } = useBgImageData(
    siteId,
    timestamp,
    robotId
  )
  const [bgImage, setBgImage] = useState<HTMLImageElement | null>(null)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const loadedImageIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!data?.url || !data.imageId) {
      setBgImage(null)
      loadedImageIdRef.current = null
      return
    }

    // 同じimageIdの場合は再読み込み不要
    if (data.imageId === loadedImageIdRef.current) {
      return
    }

    setIsImageLoading(true)
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      setBgImage(img)
      loadedImageIdRef.current = data.imageId
      setIsImageLoading(false)
    }

    img.onerror = () => {
      console.error('Failed to load background image')
      setBgImage(null)
      loadedImageIdRef.current = null
      setIsImageLoading(false)
    }

    img.src = data.url

    return () => {
      img.onload = null
      img.onerror = null
    }
  }, [data?.url, data?.imageId])

  return {
    bgImage,
    canvasParams: data?.canvasParams ?? null,
    isLoading: isMetaLoading || isImageLoading,
  }
}
